import { logWarning } from '@dx-ui/framework-logger';
import { isBrowser } from '@dx-ui/utilities-is-browser';
import { getCookie } from '@dx-ui/utilities-cookies';
import { EventEmitter } from 'events';
import type { GuestQuery } from './gql/types';
import type { AuthClientLike, WebGuestMetadataCookie } from './types';

const GUEST_METADATA_COOKIE = 'webGuestMetadata';
// Matches `requestTimeoutMs` in AuthClient.
const APP_TOKEN_TIMEOUT_MS = 30_000;
// Same keys AuthClient clears on logout (ASVS V14.3.1).
const SESSION_LOCAL_STORAGE_KEYS = ['b2bContext', 'b2bProgramId', 'bookedFor'];
const SESSION_STORAGE_KEYS = ['first_name', 'hhonors', 'email', 'firstEnroll'];

export type ServerFirstAuthState = {
  guestInfo: GuestQuery | null;
  isAuthenticated: boolean;
};

export type ServerFirstAuthClientArgs = Partial<ServerFirstAuthState> & {
  appId: string;
  appName: string;
  gqlEndpoint?: string;
};

/**
 * AuthClient for server-first apps (e.g. React Router apps that load guest data in loaders).
 *
 * Unlike `AuthClient`, it never fetches guest info or manages guest tokens client-side;
 * auth state comes from the server via `setAuthState`. It only provides the anonymous
 * app token that client-side OSC queries/mutations need, using the same session-cookie
 * rules as `AuthClient`.
 *
 * Parity with `AuthClient` is enforced by `auth-client-parity.spec.ts`.
 */
export class ServerFirstAuthClient extends EventEmitter implements AuthClientLike {
  public webGuestMetadataRequestCookie: WebGuestMetadataCookie | null = null;

  private guestInfo: GuestQuery | null;
  private isAuthenticated: boolean;
  private readonly appId: string;
  private readonly appName: string;
  private readonly gqlEndpoint: string;
  private appTokenPromise: Promise<string> | null = null;

  public constructor({
    appId,
    appName,
    gqlEndpoint = '/graphql/customer',
    guestInfo = null,
    isAuthenticated = !!guestInfo,
  }: ServerFirstAuthClientArgs) {
    super();
    if (!appId) {
      logWarning(
        'SERVER_FIRST_AUTH_CLIENT',
        new Error('Missing appId'),
        'Anonymous client requests will be sent without an app token'
      );
    }
    this.appId = appId;
    this.appName = appName;
    this.gqlEndpoint = gqlEndpoint;
    this.guestInfo = guestInfo;
    this.isAuthenticated = isAuthenticated;
  }

  // Read live so shared request code (e.g. omitting Authorization in cookie mode) never sees stale state.
  public get webGuestMetadataCookie(): WebGuestMetadataCookie | null {
    const raw = this.readMetadataCookie();
    if (!raw) return null;
    try {
      return JSON.parse(raw) as WebGuestMetadataCookie;
    } catch {
      return null;
    }
  }

  public set webGuestMetadataCookie(_value: WebGuestMetadataCookie | null) {
    // No-op: derived from document.cookie.
  }

  public setAuthState({ guestInfo, isAuthenticated }: ServerFirstAuthState) {
    this.guestInfo = guestInfo;
    this.isAuthenticated = isAuthenticated;
  }

  public getIsLoggedIn() {
    return this.isAuthenticated;
  }

  public getGuestId() {
    return this.guestInfo?.guest?.guestId ?? null;
  }

  public getGuestInfo(): Promise<GuestQuery | null> | null {
    return this.guestInfo ? Promise.resolve(this.guestInfo) : null;
  }

  public getGqlEndpoint() {
    return this.gqlEndpoint;
  }

  // No-op: sign-in is owned by the app (full reload; the server confirms the session).
  public async login() {}

  /**
   * Client-side part of sign-out only: clears client session data and emits `logout`.
   * Call it after the app has ended the server session, since `logout` notifies other tabs.
   */
  public async logout() {
    this.appTokenPromise = null;
    this.clearClientSessionData();
    this.emit('logout');
  }

  public isCorpUser() {
    return false;
  }

  public refreshGuestInfo() {
    return Promise.resolve();
  }

  public validateToken() {
    return Promise.resolve();
  }

  public updateAccessToken(_token: string) {
    // No-op: guest tokens are managed server-side.
  }

  public getAuthorizationHeader() {
    // Logged-in guests are authenticated by their session cookies.
    if (!isBrowser || !this.appId || this.getIsLoggedIn() || this.hasSessionCookie()) {
      return Promise.resolve('');
    }
    return this.appTokenPromise ?? this.startAppTokenFetch();
  }

  public refreshToken(): Promise<null> {
    // Matches AuthClient: refresh only the anonymous app token, never a guest session.
    const tokenType = this.getSessionTokenType();
    if (!isBrowser || !this.appId || this.getIsLoggedIn() || (tokenType && tokenType !== 'app')) {
      return Promise.resolve(null);
    }
    return this.startAppTokenFetch().then(() => null);
  }

  private startAppTokenFetch(): Promise<string> {
    const promise = this.fetchAppToken().then((header) => {
      // Clear only our own failed request so a stale failure can't clobber a newer refresh.
      if (!header && this.appTokenPromise === promise) {
        this.appTokenPromise = null;
      }
      return header;
    });
    this.appTokenPromise = promise;
    return promise;
  }

  // Matches AuthClient: never request an app token over an existing session cookie,
  // except an abandoned OTP (`tempLogin`) session.
  private hasSessionCookie() {
    const tokenType = this.getSessionTokenType();
    return !!tokenType && tokenType !== 'tempLogin';
  }

  // Unparseable cookies are treated as an unknown session so we never overwrite them.
  private getSessionTokenType(): string | undefined {
    if (!this.readMetadataCookie()) return undefined;
    return this.webGuestMetadataCookie?.webGuestTokenType || 'unknown';
  }

  /** Clears this tab's client session data without emitting `logout` (e.g. when another tab signed out). */
  public clearClientSessionData() {
    if (!isBrowser) return;
    // sessionStorage first: it holds the guest's personal data.
    this.clearStorage('sessionStorage', SESSION_STORAGE_KEYS);
    this.clearStorage('localStorage', SESSION_LOCAL_STORAGE_KEYS);
  }

  private clearStorage(area: 'sessionStorage' | 'localStorage', keys: string[]) {
    try {
      keys.forEach((key) => window[area].removeItem(key));
    } catch (error) {
      // Never block sign-out; usually storage is blocked, so nothing was stored.
      logWarning('SERVER_FIRST_AUTH_LOGOUT', error as Error, `Unable to clear ${area}`);
    }
  }

  private readMetadataCookie(): string | undefined {
    if (!isBrowser) return undefined;
    const cookie = getCookie(GUEST_METADATA_COOKIE);
    if (typeof cookie === 'string') {
      return cookie;
    }

    if (cookie && typeof cookie === 'object') {
      return JSON.stringify(cookie);
    }

    return undefined;
  }

  private async fetchAppToken(): Promise<string> {
    try {
      const response = await fetch(
        `/dx-customer/auth/applications/token?appName=${encodeURIComponent(this.appName)}`,
        {
          method: 'POST',
          cache: 'no-cache',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            Accept: 'application/json; charset=utf-8',
            'dx-platform': 'web',
          },
          body: JSON.stringify({ app_id: this.appId }),
          signal: AbortSignal.timeout(APP_TOKEN_TIMEOUT_MS),
        }
      );
      if (!response.ok) return '';
      // Cookie-based token flow: the token is set as a cookie, not returned in the body.
      if (this.hasSessionCookie()) return '';
      const json = (await response.json()) as { token_type?: string; access_token?: string };
      if (!json.token_type || !json.access_token) return '';
      return `${json.token_type} ${json.access_token}`;
    } catch {
      return '';
    }
  }
}
